/**
 * Ein Spaltenkopf der Bestellliste - `orderListHeader` (index.html:7318). Traegt drei Bedienelemente
 * in einem `<th>`: Sortierknopf, Facettenfilter und Breitengriff.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Die Facettenliste wird aus `facetOrders` gebildet - der Liste NACH Archiv- und Suchfilter,
 *    aber VOR den Spaltenfiltern. Ein gesetzter Filter auf Spalte A verkleinert deshalb die
 *    Auswahlliste von Spalte B NICHT; ohne diese Trennung koennte man einen einmal gesetzten
 *    Filter nicht mehr auf einen anderen Wert umstellen.
 * 2. `<details>` statt eines eigenen Klappzustands: das Menue oeffnet und schliesst der Browser.
 *    `closeOtherOrderListFilters` (index.html:7311) schliesst beim Oeffnen alle uebrigen - eine
 *    DOM-Operation, die urspruenglich als (nie verdrahtete) Prop `onFilterGeoeffnet` hier stand.
 *    Seit Auftrag "Filterfenster im Bestellboard - nur eins gleichzeitig offen" (2026-09-04) ist das
 *    NACHGEBAUT: `offen`/`onOffenAendern` (siehe unten, Eigenheit 6) statt eines eigenen States.
 * 3. `event.stopPropagation()` auf dem ganzen `<details>`: ohne das wuerde ein Klick ins Menue am
 *    Sortierknopf vorbei bis zum `<th>` laufen.
 * 4. Der Breitengriff ist ein `<span role="separator">` mit `aria-valuenow` = der GEKLEMMTEN
 *    Breite, nicht dem gespeicherten Rohwert. Task 14 hat ihn verdrahtet: `onPointerDown` und
 *    `onKeyDown` (index.html:7339) liegen als Props `onBreiteZiehen`/`onBreiteTaste` heraus, die
 *    Bedienung selbst steht in `SpaltenBreiten.ts`. Bis Task 13 war der Griff sichtbar, aber
 *    wirkungslos.
 * 5. Der Knopf „Filter entfernen" steht NUR da, wenn diese Spalte gefiltert ist.
 * 6. `.order-list-filter-menu` traegt seither zusaetzlich `hidden`, solange `<details>` geschlossen
 *    ist (Pruefung K1, Auftrag "Bestellboard-Filter datentypgerecht bauen"): geschlossener
 *    `<details>`-Inhalt ist in ECHTEN Browsern per UA-Standardstil unsichtbar UND aus dem
 *    Accessibility-Baum ausgeschlossen - jsdom bildet genau das NICHT nach (gemessen: ein
 *    `getByRole("checkbox", ...)` findet eine Checkbox in geschlossenem `<details>` trotzdem). Ohne
 *    das explizite `hidden` blieben ALLE Filter-Widgets aller Spalten - inklusive jeder
 *    Mehrfachauswahl-Checkbox mit einem Bestellungs-/Lieferantenwert als sichtbarem Text - in jedem
 *    Test ueber `getByRole`/`getByLabelText` auffindbar, obwohl `BoardSeite` app-weit dauerhaft
 *    montiert bleibt (`App.tsx`, nur eine CSS-Klasse wechselt, kein Unmount) - mit realistischem
 *    Kollisionsrisiko gegen gleichlautende Werte in voellig anderen Komponenten (Befund: ein
 *    `aria-label="BE-1"` im `RatenSammelDialog` kollidierte mit der Bestellnummer-Filteroption
 *    "BE-1"). `hidden` ist KEIN Test-Hack, sondern macht ein Verhalten explizit, das echte Browser
 *    ohnehin zeigen.
 *
 *    NACHGEBAUT (Auftrag "Filterfenster im Bestellboard - nur eins gleichzeitig offen", 2026-09-04):
 *    `<details>` war bis hierher UNGESTEUERT (kein `open`-Prop von aussen), `offen` rein lokaler
 *    State, nur per `onToggle` aus dem nativen `toggle`-Ereignis gespiegelt. Fuer „nur ein Menue
 *    gleichzeitig" MUSS eine andere Spalte ihr eigenes Menue von AUSSEN schliessen koennen - ein rein
 *    gespiegelter State kann das nicht (das naechste Oeffnen ueberschriebe seinen eigenen, nie von
 *    aussen gesetzten Wert nicht). `offen` kommt seither als Prop von `BoardSeite` (welche Spalte
 *    gerade offen ist, EINE Ebene ueber allen Spalten), `<details open={offen}>` ist damit
 *    QUASI-GESTEUERT: ein Klick auf `<summary>` toggelt die native DOM-Eigenschaft weiterhin sofort,
 *    React gleicht sie beim naechsten Render an `offen` an - stimmen native und Prop-Erwartung nicht
 *    ueberein (z. B. weil eine ANDERE Spalte inzwischen geoeffnet hat), erzwingt React das `open`
 *    OHNE Zutun des Nutzers zurueck und der Browser feuert dafuer selbst wieder ein (asynchrones)
 *    `toggle`-Ereignis - siehe `spaltenfilterOffenAendern` in `BoardSeite.tsx`, warum das schadlos
 *    bleibt. `hidden={!offen}` haengt seither DIREKT am Prop, nicht mehr am Umweg ueber `onToggle` -
 *    Klick-aussen und der explizite Schliessen-Knopf (unten) wirken dadurch OHNE auf den naechsten
 *    `toggle`-Tick warten zu muessen.
 *
 * ================= Ein Widget pro Datentyp (Auftrag "Bestellboard-Filter datentypgerecht bauen",
 * 2026-09-01) - NEUBAU ohne Bestandsvorbild =====================================================
 *
 * Bis hierher war das Filtermenue IMMER ein natives `<select>` mit Exakttreffer, unabhaengig vom
 * tatsaechlichen Datentyp der Spalte. `orderListFilterKind(key)` (`lib/orderList.ts`) entscheidet
 * seither, welches der fuenf Widgets unter `components/Board/filter/` hier steht:
 *
 * | Kind      | Widget                  | Optionsquelle                                             |
 * |-----------|--------------------------|-----------------------------------------------------------|
 * | "date"    | `DatumsBereichFilter`    | -                                                           |
 * | "money"   | `BetragsBereichFilter`   | `orderListMoneyWaehrungen(facetOrders, lieferanten)`        |
 * | "boolean" | `BooleanFilter`          | -                                                           |
 * | "eingebucht" | `EingebuchtFilter`    | - (Spalte „Ware eingebucht", 2026-10-20: Datum + Zustaende)  |
 * | "enum"    | `MehrfachauswahlFilter`  | `orderListStatusOptionen(definitionen)` (KANONISCH, alle)   |
 * | "text"    | `MehrfachauswahlFilter`  | `orderListFacetOptions(...)` (aus `facetOrders`, Eigenheit 1)|
 *
 * `filterwert`/`onFiltern` sind seither UNTYPISIERT bezogen auf den konkreten Datentyp
 * (`Spaltenfilterwert | undefined`) - welche Form gerade gilt, folgt ausschliesslich aus `kind`.
 */
import { useEffect, useRef } from "react";
import {
  orderListColumnSize,
  orderListColumnWidth,
  orderListFacetOptions,
  orderListFilterKind,
  orderListMoneyWaehrungen,
  orderListSpaltenfilterAktiv,
  orderListStatusOptionen,
} from "../../lib/orderList.ts";
import type {
  Bereichsfilter,
  EingebuchtFilter as EingebuchtFilterWert,
  OrderListWidths,
  Spaltenfilterwert,
} from "../../lib/orderList.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import type { Order, Supplier } from "../../lib/types.ts";
import { Ikone } from "../Ikone.tsx";
import { MehrfachauswahlFilter } from "./filter/MehrfachauswahlFilter.tsx";
import { DatumsBereichFilter } from "./filter/DatumsBereichFilter.tsx";
import { BetragsBereichFilter } from "./filter/BetragsBereichFilter.tsx";
import { BooleanFilter } from "./filter/BooleanFilter.tsx";
import { EingebuchtFilter } from "./filter/EingebuchtFilter.tsx";

export interface SpaltenkopfProps {
  label: string;
  schluessel: string;
  /** Die Liste nach Archiv- und Suchfilter, VOR den Spaltenfiltern - siehe Eigenheit 1. */
  facetOrders: readonly Order[];
  definitionen: StatusDefinitionen;
  lieferanten: readonly Supplier[] | null | undefined;
  breiten: OrderListWidths;
  sortierung: { key: string; direction: string };
  filterwert: Spaltenfilterwert | undefined;
  onSortieren: (schluessel: string) => void;
  onFiltern: (schluessel: string, wert: Spaltenfilterwert | undefined) => void;
  onFilterLeeren: (schluessel: string) => void;
  /**
   * Ob DIESE Spalte gerade ihr Filtermenue offen hat - koordiniert `BoardSeite` ueber alle Spalten
   * hinweg (siehe Eigenheit 6), damit nie zwei gleichzeitig offen stehen.
   */
  offen: boolean;
  /**
   * Meldet den naechsten gewuenschten Offen-Zustand nach oben - ausgeloest vom nativen
   * `<summary>`-Klick (`onToggle`), einem Klick ausserhalb des Menues oder dem expliziten
   * Schliessen-Knopf darin (siehe Eigenheit 6 und Eigenheit 2).
   */
  onOffenAendern: (offen: boolean) => void;
  /**
   * `startOrderListColumnResize(event, key)` (index.html:7339) - Task 14. Ohne Verdrahtung ist der
   * Griff sichtbar, aber wirkungslos (so stand er nach Task 13 hier).
   */
  onBreiteZiehen?: (ereignis: React.PointerEvent<HTMLElement>, schluessel: string) => void;
  /** `resizeOrderListColumnByKey(event, key)` (index.html:7339) - Task 14. */
  onBreiteTaste?: (ereignis: React.KeyboardEvent<HTMLElement>, schluessel: string) => void;
}

export function Spaltenkopf({
  label,
  schluessel: key,
  facetOrders,
  definitionen,
  lieferanten,
  breiten,
  sortierung,
  filterwert,
  onSortieren,
  onFiltern,
  onFilterLeeren,
  offen,
  onOffenAendern,
  onBreiteZiehen,
  onBreiteTaste,
}: SpaltenkopfProps) {
  const sortActive = sortierung.key === key;
  const sortIcon = sortActive
    ? sortierung.direction === "asc"
      ? "arrow-up"
      : "arrow-down"
    : "arrow-up-down";
  const size = orderListColumnSize(key);
  const kind = orderListFilterKind(key);
  const aktiv = orderListSpaltenfilterAktiv(key, filterwert);
  // Traegt `<details>` fuer den Klick-aussen-Vergleich unten (Eigenheit 6) - dasselbe Element, an das
  // auch `ref={detailsRef}` weiter unten haengt.
  const detailsRef = useRef<HTMLDetailsElement>(null);

  /**
   * Klick ausserhalb des offenen Menues schliesst es (Auftrag "Filterfenster im Bestellboard - nur
   * eins gleichzeitig offen", Punkt 2). Der Listener haengt NUR waehrend `offen` ist am `document` -
   * sauber abgemeldet ueber das `useEffect`-Aufraeumen, kein Leck bei wiederholtem Oeffnen/Schliessen.
   * `detailsRef.current?.contains(...)` schliesst sowohl das Filter-Icon (`<summary>`, Punkt 2 im
   * Auftrag: „NICHT reagieren, wenn der Klick auf das Filter-Icon selbst trifft") als auch jeden
   * Klick INNERHALB des Menues aus (z. B. das Suchfeld der Mehrfachauswahl) - beide sind Nachfahren
   * desselben `<details>`.
   *
   * NACHBESSERUNG (Pruefung, KRITISCH): registriert in der CAPTURE-Phase (dritter Parameter `true`
   * bei add-/removeEventListener), NICHT in der Bubble-Phase, in der ein `document`-Listener zuerst
   * stand. Gemessen: ein Klick auf eine Bestellzeile schloss das offene Menue NICHT - `Bestellzeile.tsx`
   * ruft an mehreren Stellen `ereignis.stopPropagation()` (`.order-list-editable-value`, Zeile ~252;
   * der Bestellnummer-Tieflink-Anker, Zeile ~504-510), die die Ausbreitung schon VOR `document`
   * beendet, wenn der Listener dort in der (spaeteren) Bubble-Phase haengt. Capture-Phase-Listener auf
   * `document` laufen dagegen VOR jedem `stopPropagation()` an einem Kindelement (Capture laeuft
   * Wurzel -> Ziel, Bubble erst danach Ziel -> Wurzel) - der Klick wird dadurch unabhaengig davon
   * erkannt, ob das angeklickte Element selbst die Ausbreitung stoppt. `detailsRef.contains(...)`
   * bleibt die richtige Ausschlusspruefung: sie greift in BEIDEN Phasen gleich, weil sie auf
   * `ereignis.target` prueft (dem tatsaechlich angeklickten Element), nicht auf die Phase.
   */
  useEffect(() => {
    if (!offen) return;
    const aufAussenKlick = (ereignis: MouseEvent): void => {
      if (detailsRef.current?.contains(ereignis.target as Node)) return;
      onOffenAendern(false);
    };
    document.addEventListener("click", aufAussenKlick, true);
    return () => document.removeEventListener("click", aufAussenKlick, true);
  }, [offen, onOffenAendern]);

  let widget: React.ReactNode;
  if (kind === "boolean") {
    widget = (
      <BooleanFilter
        label={label}
        wert={typeof filterwert === "boolean" ? filterwert : undefined}
        onAendern={(wert) => onFiltern(key, wert)}
      />
    );
  } else if (kind === "eingebucht") {
    widget = (
      <EingebuchtFilter
        label={label}
        filter={(filterwert as EingebuchtFilterWert | undefined) ?? {}}
        onAendern={(filter) => onFiltern(key, filter)}
      />
    );
  } else if (kind === "date") {
    widget = (
      <DatumsBereichFilter
        label={label}
        bereich={(filterwert as Bereichsfilter | undefined) ?? {}}
        onAendern={(bereich) => onFiltern(key, bereich)}
      />
    );
  } else if (kind === "money") {
    const bereiche = (filterwert as Record<string, Bereichsfilter> | undefined) ?? {};
    widget = (
      <BetragsBereichFilter
        label={label}
        waehrungen={orderListMoneyWaehrungen(facetOrders, lieferanten)}
        bereiche={bereiche}
        onAendern={(waehrung, bereich) => onFiltern(key, { ...bereiche, [waehrung]: bereich })}
      />
    );
  } else {
    const optionen =
      kind === "enum"
        ? orderListStatusOptionen(definitionen)
        : orderListFacetOptions(key, facetOrders, definitionen, lieferanten);
    widget = (
      <MehrfachauswahlFilter
        label={label}
        optionen={optionen}
        ausgewaehlt={(filterwert as string[] | undefined) ?? []}
        onAendern={(werte) => onFiltern(key, werte)}
      />
    );
  }

  return (
    <th data-order-list-column={key}>
      <div className="order-list-th">
        <button
          className="order-list-sort-btn"
          type="button"
          onClick={() => onSortieren(key)}
          title={`Nach ${label} sortieren`}
        >
          <span>{label}</span>
          <span className="order-list-sort">
            <Ikone name={sortIcon} />
          </span>
        </button>
        <details
          ref={detailsRef}
          className={`order-list-filter ${aktiv ? "active" : ""}`}
          open={offen}
          onClick={(ereignis) => ereignis.stopPropagation()}
          onToggle={(ereignis) => onOffenAendern((ereignis.target as HTMLDetailsElement).open)}
        >
          <summary title={`${label} filtern`} aria-label={`${label} filtern`}>
            <Ikone name="filter" />
          </summary>
          <div className="order-list-filter-menu" hidden={!offen}>
            {/* NACHBESSERUNG (Pruefung, "bei Gelegenheit"): `<Ikone name="x" />` statt eines rohen
                "×"-Glyphs - Haus-Konvention fuer Schliessen-Knoepfe, siehe
                `Drawer/TasksTab/AufgabenDetail.tsx:378-385`. `aria-label` dort knapp gehalten
                ("Detailfenster schließen"); hier statt des sperrigen "${label} filtern schließen"
                (zwei Verben hintereinander) ebenso knapp, aber je Spalte eindeutig. */}
            <button
              type="button"
              className="spaltenfilter-schliessen"
              aria-label={`${label}-Filter schließen`}
              onClick={() => onOffenAendern(false)}
            >
              <Ikone name="x" />
            </button>
            <label>{`${label} filtern`}</label>
            {widget}
            {aktiv ? (
              <button type="button" onClick={() => onFilterLeeren(key)}>
                Filter entfernen
              </button>
            ) : null}
          </div>
        </details>
        <span
          className="order-list-column-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label={`Spalte ${label} in der Breite ändern`}
          aria-valuemin={size.min}
          aria-valuemax={size.max}
          aria-valuenow={orderListColumnWidth(key, breiten)}
          tabIndex={0}
          title="Ziehen oder mit den Pfeiltasten ändern"
          onPointerDown={(ereignis) => onBreiteZiehen?.(ereignis, key)}
          onKeyDown={(ereignis) => onBreiteTaste?.(ereignis, key)}
        />
      </div>
    </th>
  );
}
